import * as React from 'react';
import type {
  QuickAddOfferDraftOption,
  QuickAddOfferPickerLockedTarget,
  QuickAddOfferPickerMode,
  QuickAddOfferPickerProps,
} from './quick-add-offer-picker';

function getDefaultMode(props: QuickAddOfferPickerProps): QuickAddOfferPickerMode {
  // Locked target: always summary, never draft-list/create — the destination
  // cannot be changed from within the picker. Takes priority over `initialMode`.
  if (props.lockedTarget != null) {
    return 'summary';
  }

  if (props.initialMode != null) {
    return props.initialMode;
  }

  const hasEditableDrafts = props.drafts.some((draft) => draft.editable);

  // Only enter summary mode when the selected draft is still in the editable drafts list.
  // If the stored last-used draft was trashed, it won't appear in drafts — fall back to
  // 'changing' so the user sees the available drafts instead of an empty summary card.
  const selectedDraftIsAvailable =
    props.selectedDraftId != null && props.drafts.some((d) => d.id === props.selectedDraftId);

  if (selectedDraftIsAvailable && hasEditableDrafts) {
    return 'summary';
  }

  return hasEditableDrafts ? 'changing' : 'creating';
}

export function useQuickAddOfferPickerState(props: QuickAddOfferPickerProps) {
  const [mode, setMode] = React.useState<QuickAddOfferPickerMode>(() => getDefaultMode(props));
  const [internalSelectedDraftId, setInternalSelectedDraftId] = React.useState<string | null>(
    props.selectedDraftId,
  );
  const hasEditableDrafts = props.drafts.some((draft) => draft.editable);
  // Tracks the previous `hasEditableDrafts` value across renders so the
  // force-to-'creating' effect below can fire only on a genuine live
  // disappearance of editable drafts (e.g. the preselected draft gets
  // trashed by another user while this picker is open), not on every render
  // where `hasEditableDrafts` already happens to be false. Without this
  // guard, a caller opened with zero editable drafts from the very start
  // (or with an explicit `initialMode="changing"`) could never navigate to
  // 'changing'/'summary' — any user-initiated `setMode` call was immediately
  // snapped back to 'creating' on the next render, permanently hiding the
  // "Wróć do istniejących" back affordance's destination and the
  // `noDraftsLabel`/`noPermissionLabel` empty states (CU bug: quick-add
  // picker traps the user in create-new-offer mode with no way back).
  const hadEditableDraftsRef = React.useRef(hasEditableDrafts);

  React.useEffect(() => {
    setInternalSelectedDraftId(props.selectedDraftId);
  }, [props.selectedDraftId]);

  React.useEffect(() => {
    const hadEditableDrafts = hadEditableDraftsRef.current;
    hadEditableDraftsRef.current = hasEditableDrafts;

    // A locked target must never fall into 'creating', even with zero OTHER
    // editable drafts — the synthesized locked draft (see `deriveEffectiveProps`)
    // already keeps `hasEditableDrafts` true, but this short-circuit is explicit
    // defense against that invariant changing.
    if (props.lockedTarget != null) {
      return;
    }
    // Only force 'creating' on the true -> false transition (drafts just
    // disappeared out from under an open picker). Do NOT re-fire just
    // because `hasEditableDrafts` was already false and the user (or an
    // explicit `initialMode`) moved to a different mode — that would trap
    // navigation with no way to ever see the empty-drafts state.
    if (hadEditableDrafts && !hasEditableDrafts && mode !== 'creating') {
      setMode('creating');
    }
  }, [props.lockedTarget, hasEditableDrafts, mode]);

  return {
    mode,
    setMode,
    selectedDraftId: props.onSelectDraft ? props.selectedDraftId : internalSelectedDraftId,
    handleSelectDraft: (draftId: string | null) => {
      if (draftId == null) {
        setInternalSelectedDraftId(null);
        props.onSelectDraft?.(null);
        return;
      }

      setInternalSelectedDraftId(draftId);
      props.onSelectDraft?.(draftId);
      setMode('summary');
    },
    clearSelectedDraft: () => {
      setInternalSelectedDraftId(null);
      props.onSelectDraft?.(null);
    },
  };
}

function buildLockedDraft(
  lockedTarget: QuickAddOfferPickerLockedTarget,
  mediaUnitIds: Array<string | number> | undefined,
): QuickAddOfferDraftOption {
  return {
    id: lockedTarget.offerId,
    title: lockedTarget.offerName,
    clientName: '',
    unitCount: (mediaUnitIds ?? []).length,
    editable: true,
  };
}

/**
 * When `lockedTarget` is set, synthesize a single-entry `drafts` array + a
 * matching `selectedDraftId` so the rest of the picker's existing summary-mode
 * resolution (`selectedDraft.title`, confirm handler) works unchanged instead
 * of branching on "locked" everywhere it reads a draft.
 */
export function deriveEffectiveProps(props: QuickAddOfferPickerProps): QuickAddOfferPickerProps {
  if (props.lockedTarget == null) {
    return props;
  }

  return {
    ...props,
    drafts: [buildLockedDraft(props.lockedTarget, props.mediaUnitIds)],
    selectedDraftId: props.lockedTarget.offerId,
  };
}
